import React from 'react';
import type { Draft } from 'immer';
import { AutoRotateType, type SceneConfigAutoRotate, SceneType } from '@types';
import { Collapse } from '@components/Collapse';
import { $sceneMapApi } from '@stores/dataStores';
import Slider from '@components/Slider';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import type { SidebarAutoRotateProps } from './types';
import { krpRemoveCustomRotate } from '../actions/krpRotate';
import { CustomRotate } from './CustomRotate';

const typeItems: DropdownItem<AutoRotateType>[] = [
  { id: AutoRotateType.none, name: '固定停留' },
  { id: AutoRotateType.custom, name: '自定义' },
];

export function SidebarAutoRotateMatrix({
  sceneId,
  data,
}: SidebarAutoRotateProps) {
  const { type, stayTime } = data;

  const updateAutoRotate = (
    updater: (
      draft: Draft<SceneConfigAutoRotate>
    ) => SceneConfigAutoRotate | void
  ) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        updater(draft.config.autoRotate!);
      },
    });
  };

  const handleStayTimeChange = (val: number) => {
    updateAutoRotate((draft) => {
      draft.stayTime = val;
    });
  };

  const handleTypeChange = (typeId: AutoRotateType) => {
    updateAutoRotate((draft) => {
      draft.type = typeId;
      if (type === AutoRotateType.custom) {
        krpRemoveCustomRotate();
      }
      draft.stayTime = 30;
      draft.tourData = [];
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [SceneType.matrix],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.config.autoRotate = {
          ...draft.get(_id)!.config.autoRotate,
          type,
          stayTime,
        };
      });
    });
    sendSuccess('导览方式已应用到所选场景');
  };

  return (
    <Collapse title="导览方式">
      <Dropdown
        className="mb-20"
        selected={type}
        items={typeItems}
        onChange={handleTypeChange}
      />
      {type === AutoRotateType.none && (
        <Slider
          min={3}
          max={720}
          valToString={(val) => `${val}秒`}
          value={stayTime}
          onChange={handleStayTimeChange}
        >
          <Slider.Header title="停留时长" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
      )}
      {type === AutoRotateType.custom && <CustomRotate />}
      {type !== AutoRotateType.custom && (
        <SidebarApplyTo
          title="把导览方式应用到..."
          className="mt-20"
          onClick={handleApplyTo}
        />
      )}
    </Collapse>
  );
}
